import { useState } from 'react';
import Image from 'next/image';

import { Box, Circle, Flex, Heading, Link, Skeleton, Stack, Text, useRadioGroup } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { useMastersPrice } from '~/features/accreditation/hooks';
import {
  AccreditationColorScheme,
  accreditationGradients,
  AccreditationPrerequisitesProps,
} from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { CheckIcon, CircleArrowRightIcon, CircleCheckIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { BraavosPlanPrices } from '~/features/plans/models/braavos-plan-prices';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { RPL_ANALYTICS_LABEL, RPL_WOOLF_FAQ_HREF } from '~/features/recognition-of-prior-learning/constants';
import { useClientRender } from '~/hooks/use-client-render';

import { shouldSeeAnnualBundlePlan } from '../../checkout/utils/should-see-annual-plan';

import { SubscriptionOption } from './subscription-option';

function getCheckoutUrl(
  hasActiveSubscription: boolean,
  formalDegreeKey: FormalDegreeKey,
  selectedBillingFrequency: BillingFrequency = billingFrequencySchema.enum['monthly']
) {
  if (hasActiveSubscription) {
    return `/checkout/degree-application/purchase/credit-card?formalDegreeKey=${formalDegreeKey}`;
  }
  return `/checkout/individual/${selectedBillingFrequency}/credit-card?degreeApplication=true&formalDegreeKey=${formalDegreeKey}`;
}

export function AccreditationPrerequisitesSectionContent({
  colorScheme = 'blue',
  formalDegreeKey,
  degreeTitle,
  feeDescription,
  prerequisites,
  stampImage,
  initialBillingFrequency,
}: {
  colorScheme?: AccreditationColorScheme;
  formalDegreeKey: FormalDegreeKey;
  initialBillingFrequency?: BillingFrequency | null;
} & AccreditationPrerequisitesProps) {
  const gradients = accreditationGradients[colorScheme];
  const clientReady = useClientRender();
  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;
  const { currency } = useCurrency();
  const { data: mastersPrice } = useMastersPrice(currency);
  const { data: pricePlans } = usePricePlans(currency);
  const isBundleAnnualPlan = shouldSeeAnnualBundlePlan(currency);
  const [selectedBillingFrequency, setSelectedBillingFrequency] = useState<BillingFrequency>(
    initialBillingFrequency ??
      (isBundleAnnualPlan ? billingFrequencySchema.enum['annually'] : billingFrequencySchema.enum['4month'])
  );
  const isBundleSelected =
    selectedBillingFrequency === billingFrequencySchema.enum['4month'] ||
    selectedBillingFrequency === billingFrequencySchema.enum['annually'];

  const monthlyPlan = pricePlans?.prices['monthly']?.individual;
  const bundlePlan = pricePlans?.prices[isBundleAnnualPlan ? 'annually' : '4month']?.individual;

  const monthlyPrice = monthlyPlan?.displayPrice;
  const strikeThroughMonthlyPrice = monthlyPlan?.strikeThroughDisplayPrice;

  const subscriptionPrice = isBundleSelected ? bundlePlan?.displayPricePerMonth : monthlyPrice;
  const subscriptionStrikeThroughPrice = isBundleSelected
    ? bundlePlan?.strikeThroughDisplayPricePerMonth
    : strikeThroughMonthlyPrice;

  function handleApplyNowClick() {
    trackEvent({
      type: 'click',
      name: 'Masters Page CTA Clicked',
      label: 'mid_enroll_now',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
  }

  return (
    <ContentContainer as='section' contentMaxWidth='content-max-width-md' mobilePadding='0' position='relative'>
      <Flex
        bgGradient={gradients.prerequisitesBg}
        borderRadius={{ base: '0', md: '12px' }}
        color='white'
        flexDirection='column'
        justifyContent='center'
        overflow='hidden'
        padding={{ base: '32px 16px', md: '48px 56px' }}
        position='relative'
        gap={{
          base: '32px',
          md: '40px',
        }}
      >
        <Background />
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          gap='40px'
          alignItems={{
            md: 'flex-start',
          }}
        >
          <Flex
            alignSelf='normal'
            direction='column'
            gap={8}
            justifyContent='space-between'
            position='relative'
            flex={{
              md: '1 1 296px',
            }}
          >
            <Flex flexDirection='column' gap='32px'>
              <Flex flexDirection='column' gap='16px'>
                <Flex alignItems='center' gap='8px'>
                  <CircleCheckIcon color='gold.300' h={{ base: '16px', md: '18px' }} w={{ base: '16px', md: '18px' }} />
                  <Heading
                    as='p'
                    color='gold.300'
                    fontFamily='condensed'
                    fontSize={{ base: '0.8125rem', md: '0.9375rem' }}
                    fontWeight='500'
                    letterSpacing='.8px'
                    lineHeight='1'
                    textTransform='uppercase'
                  >
                    Prerequisites
                  </Heading>
                  <Box bg='gold.500' flex={1} h='.5px' />
                </Flex>
                <Heading as='h2' size={{ base: 'mobile-heading-3', md: 'heading-3' }}>
                  What you’ll need to get started
                </Heading>
              </Flex>
              <Flex direction='column' gap='12px'>
                {prerequisites.map((prerequisite) => (
                  <Flex key={prerequisite} alignItems={{ base: 'flex-start', md: 'center' }} gap='10px'>
                    <CheckIcon color={`${colorScheme}.300`} height='18px' width='18px' />
                    <Text color={`${colorScheme}.100`}>{prerequisite}</Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
            <ExperienceDegreeCreditsMessaging colorScheme={colorScheme} formalDegreeKey={formalDegreeKey} />
          </Flex>
          <Box
            display='flex'
            flexDirection='column'
            position='relative'
            flex={{
              md: '0 0 360px',
            }}
          >
            <Box
              bgGradient={gradients.prerequisitesBorder}
              borderRadius='8px'
              bottom='-1px'
              left='-1px'
              position='absolute'
              right='-1px'
              top='-1px'
            />
            <Flex bg='#0A0B0F' borderRadius='8px' direction='column' flex={1} gap='8px' p='8px' position='relative'>
              <Flex
                bg='white'
                border='1px solid #ECECEC'
                borderRadius='4px'
                direction='column'
                gap='16px'
                p='24px'
                position='relative'
              >
                <Flex borderBottom='1px solid #F2F2F2' direction='column' gap='12px' pb='20px' position='relative'>
                  <Flex direction='column' gap='4px'>
                    <Heading
                      as='h3'
                      color='gold.500'
                      fontSize='1.5rem'
                      fontWeight='600'
                      lineHeight='1.14'
                      size='heading-8'
                    >
                      {degreeTitle}
                    </Heading>
                    <Text color='grey.900' fontSize='0.8125rem' fontWeight='500' lineHeight='1.4'>
                      {feeDescription}{' '}
                      <Box as='span' color='grey.500' fontWeight='400'>
                        (non refundable)
                      </Box>
                    </Text>
                  </Flex>
                  <Flex alignItems='flex-end' flexWrap='wrap' gap='8px'>
                    <Heading as='p' color='grey.900' size='heading-4'>
                      {mastersPrice || <Skeleton as='span' display='block' height='42px' width='85.15px' />}
                    </Heading>
                    <Text color='grey.500'>/one payment</Text>
                  </Flex>
                </Flex>
                <Flex direction='column' gap='8px'>
                  <Flex alignItems='center' justifyContent='space-between'>
                    <Flex gap='4px'>
                      <Heading color={`${colorScheme}.700`} size='heading-7'>
                        Udacity subscription
                      </Heading>
                      <Flex
                        alignItems='center'
                        border='1px solid'
                        borderColor='grey.500'
                        borderRadius='full'
                        color='grey.500'
                        fontSize='.5rem'
                        fontWeight='600'
                        h='12px'
                        justifyContent='center'
                        w='12px'
                      >
                        1
                      </Flex>
                    </Flex>
                  </Flex>
                  <SubscriptionRadioGroup
                    bundlePlan={bundlePlan}
                    colorScheme={colorScheme}
                    currency={currency}
                    monthlyPlan={monthlyPlan}
                    selectedBillingFrequency={selectedBillingFrequency}
                    onBillingFrequencyChange={setSelectedBillingFrequency}
                  />
                </Flex>
                <Box position='absolute' right='0' top='0'>
                  <Image alt='' height={134} src={stampImage} style={{ pointerEvents: 'none' }} width={100} />
                </Box>
              </Flex>
              <ButtonLink
                buttonProps={{
                  leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                  size: 'icon-left',
                  variant: 'solid',
                  colorScheme: 'blue',
                  onClick: handleApplyNowClick,
                }}
                linkProps={{
                  href: getCheckoutUrl(hasActiveSubscription ?? false, formalDegreeKey, selectedBillingFrequency),
                }}
              >
                Enroll Now
              </ButtonLink>
            </Flex>
          </Box>
        </Flex>
      </Flex>
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap='8px'
        justifyContent='center'
        mt={{ base: '0', md: '8px' }}
        p='16px'
      >
        <Flex
          alignItems='center'
          border='1px solid'
          borderColor='grey.900'
          borderRadius='full'
          color='grey.900'
          flexShrink={0}
          fontSize='.5rem'
          fontWeight='600'
          h='12px'
          justifyContent='center'
          w='12px'
        >
          1
        </Flex>
        <Text color='grey.500' fontSize='.625rem'>
          Subscription billed at {getPriceText(subscriptionStrikeThroughPrice, subscriptionPrice)} /month
          {!isBundleSelected && '.'}
          {isBundleSelected && (
            <>
              {' '}
              for the first {isBundleAnnualPlan ? 'year' : 'four months'}, then at{' '}
              {getPriceText(strikeThroughMonthlyPrice, monthlyPrice)} /month.
            </>
          )}
        </Text>
      </Flex>
    </ContentContainer>
  );
}

function getPriceText(strikeThroughPrice: string | null | undefined, price: string | null | undefined) {
  if (strikeThroughPrice) {
    return (
      <>
        <Box as='del'>{strikeThroughPrice}</Box> <Box as='ins'>{price}</Box>
      </>
    );
  }
  return price;
}

export function Background() {
  return (
    <Box display={{ base: 'none', md: 'block' }} opacity='.35' position='absolute' right='40px' top='50px'>
      <Circle
        border='solid 1px'
        borderColor='gold.500'
        left='50%'
        position='absolute'
        size='308px'
        top='50%'
        transform='translate(-50%, -50%)'
      />
      <Circle
        border='solid 1px'
        borderColor='gold.500'
        left='50%'
        position='absolute'
        size='538px'
        top='50%'
        transform='translate(-50%, -50%)'
      />
      <Circle
        border='solid 1px'
        borderColor='gold.500'
        left='50%'
        position='absolute'
        size='782px'
        top='50%'
        transform='translate(-50%, -50%)'
      />
      <Circle
        border='solid 1px'
        borderColor='gold.500'
        left='50%'
        position='absolute'
        size='1042px'
        top='50%'
        transform='translate(-50%, -50%)'
      />
    </Box>
  );
}

function SubscriptionRadioGroup({
  bundlePlan,
  colorScheme = 'blue',
  monthlyPlan,
  onBillingFrequencyChange,
  selectedBillingFrequency,
  currency,
}: {
  colorScheme?: AccreditationColorScheme;
  bundlePlan:
    | BraavosPlanPrices['prices']['annually']['individual']
    | BraavosPlanPrices['prices']['4month']['individual']
    | undefined;
  monthlyPlan: BraavosPlanPrices['prices']['monthly']['individual'] | undefined;
  onBillingFrequencyChange: (frequency: BillingFrequency) => void;
  selectedBillingFrequency: BillingFrequency;
  currency: string;
}) {
  const isBundleAnnualPlan = shouldSeeAnnualBundlePlan(currency);
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'billing-frequency',
    onChange: (value) => onBillingFrequencyChange(value as BillingFrequency),
    value: selectedBillingFrequency,
  });

  const group = getRootProps();

  return (
    <Stack direction='column' gap='12px' {...group}>
      <SubscriptionOption
        {...getRadioProps({
          value: isBundleAnnualPlan ? billingFrequencySchema.enum['annually'] : billingFrequencySchema.enum['4month'],
        })}
        ariaLabel={isBundleAnnualPlan ? 'Annual Prepay' : '4-Months Prepay'}
        colorScheme={colorScheme}
        couponPercentage={bundlePlan?.couponPercentOff ?? 0}
        label={isBundleAnnualPlan ? 'Annual Prepay' : '4-Months Prepay'}
        price={bundlePlan?.displayPricePerMonth}
        strikeThroughPrice={bundlePlan?.strikeThroughDisplayPricePerMonth}
      />
      <SubscriptionOption
        {...getRadioProps({ value: billingFrequencySchema.enum.monthly })}
        ariaLabel='Monthly Payment'
        colorScheme={colorScheme}
        couponPercentage={monthlyPlan?.couponPercentOff ?? 0}
        label='Monthly Payment'
        price={monthlyPlan?.displayPrice}
        strikeThroughPrice={monthlyPlan?.strikeThroughDisplayPrice}
      />
    </Stack>
  );
}

function ExperienceDegreeCreditsMessaging({
  colorScheme = 'blue',
  formalDegreeKey,
}: {
  colorScheme?: AccreditationColorScheme;
  formalDegreeKey: FormalDegreeKey;
}) {
  return (
    <Flex
      backdropFilter='blur(4px)'
      bgColor='rgba(0, 0, 0, 0.32)'
      borderRadius='6px'
      overflow='hidden'
      p='16px'
      position='relative'
      sx={{
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '6px',
          padding: '1px',
          background: 'linear-gradient(74.36deg, rgba(237, 169, 59, 0.4) 12.12%, rgba(237, 169, 59, 0) 47.66%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
      }}
    >
      <Box>
        <Heading
          as='p'
          color='gold.300'
          display='flex'
          fontSize='1.125rem'
          fontWeight='600'
          letterSpacing='-0.5px'
          lineHeight='1.2'
          mb={{
            base: 4,
            md: 1,
          }}
        >
          Earn up to 30% of your degree credits from your experience
        </Heading>
        <Text color={`${colorScheme}.100`} fontSize='0.8125rem' lineHeight='1.4'>
          Don&apos;t start from scratch. Your professional and learning history can earn you credits needed to graduate.{' '}
          <Link
            _active={{ color: 'gold.300' }}
            _hover={{ color: 'gold.100' }}
            _visited={{ color: 'gold.300' }}
            alignItems='center'
            color='gold.300'
            display='inline-flex'
            fontSize='inherit'
            fontWeight='inherit'
            gap='2px'
            href={RPL_WOOLF_FAQ_HREF}
            lineHeight='inherit'
            rel='noopener noreferrer'
            target='_blank'
            textDecoration='underline'
            verticalAlign='baseline'
            whiteSpace='nowrap'
            isExternal
            onClick={() => {
              trackEvent({
                additionalAttributes: {
                  formal_degree_key: formalDegreeKey,
                },
                label: RPL_ANALYTICS_LABEL.learnMore,
                name: 'Masters Page CTA Clicked',
                type: 'click',
              });
            }}
          >
            Learn more
            <ArrowRightIcon h='1em' w='1em' />
          </Link>
        </Text>
      </Box>
    </Flex>
  );
}

export function AccreditationPrerequisitesSection({
  colorScheme = 'blue',
  formalDegreeKey,
  ...contentProps
}: {
  colorScheme?: AccreditationColorScheme;
  formalDegreeKey: FormalDegreeKey;
} & AccreditationPrerequisitesProps) {
  const promo = usePromotion();
  const initialBillingFrequency = promo.data?.defaultSelectedPlan;
  return (
    <AccreditationPrerequisitesSectionContent
      key={initialBillingFrequency}
      colorScheme={colorScheme}
      formalDegreeKey={formalDegreeKey}
      initialBillingFrequency={initialBillingFrequency}
      {...contentProps}
    />
  );
}
